import type { GameState } from '../game/GameState';
import { Sandbox } from '../game/programming/Sandbox';
import { CodeEditor } from '../ui/CodeEditor';
import { ConsoleOutput } from '../ui/ConsoleOutput';
import { Sidebar } from '../ui/Sidebar';
import { StockChart } from '../ui/StockChart';

export class GameEngine {
  private gameState: GameState;
  private sandbox: Sandbox;
  private consoleOutput: ConsoleOutput;
  private sidebar: Sidebar;
  private editor: CodeEditor;
  private runBtn: HTMLButtonElement;
  private stockChart: StockChart;
  private executing = false;

  constructor(gameState: GameState) {
    this.gameState = gameState;
    this.sandbox = new Sandbox();

    const consoleEl = document.getElementById('console-panel') as HTMLDivElement;
    this.consoleOutput = new ConsoleOutput(consoleEl);

    document.getElementById('clear-console-btn')!.addEventListener('click', () => {
      this.consoleOutput.clear();
    });

    const sidebarEl = document.getElementById('sidebar') as HTMLDivElement;
    this.sidebar = new Sidebar(sidebarEl, gameState, this.consoleOutput);

    this.sidebar.setOnLoadMission((mission) => {
      this.consoleOutput.appendSystem(`--- MISSION: ${mission.name} ---`);
      this.consoleOutput.appendLog(mission.description);
      this.consoleOutput.appendLog(`Hint: ${mission.hint}`);
      this.consoleOutput.appendLog(`Reward: ${mission.researchCredits} research credits` + (mission.moneyReward > 0 ? ` + $${mission.moneyReward.toLocaleString()}` : ''));
      if (mission.collectCost) this.consoleOutput.appendLog(`Collection cost: $${mission.collectCost.toLocaleString()}`);
    });

    this.sidebar.setOnLoadMissionCode((mission) => {
      if (this.executing) this.stopExecution();
      this.editor.setCode(mission.starterCode);
      this.consoleOutput.appendSystem(`Loaded starter code for: ${mission.name}`);
    });

    const editorContainer = document.getElementById('editor-container') as HTMLDivElement;
    this.editor = new CodeEditor(editorContainer, (code) => {
      if (this.executing) {
        this.stopExecution();
      } else {
        this.runCode(code);
      }
    });

    this.editor.setOnTabsChanged(() => this.updateFileLabel());
    this.updateFileLabel();
    this.setupFileManager();

    this.sidebar.setOnInsertCode((code) => {
      this.editor.insertAtCursor(code);
    });

    this.sidebar.setOnGetCode(() => {
      return this.editor.getCode();
    });

    this.sidebar.setOnCollectMission((missionId) => {
      this.doCollectMission(missionId);
    });

    this.runBtn = document.getElementById('run-btn') as HTMLButtonElement;
    this.runBtn.addEventListener('click', () => {
      if (this.executing) {
        this.stopExecution();
      } else {
        this.runCode(this.editor.getCode());
      }
    });

    const chartCanvas = document.getElementById('stock-chart-canvas') as HTMLCanvasElement;
    this.stockChart = new StockChart(chartCanvas);
    this.setupChartControls();
    this.setupTabs();
  }

  private setupChartControls(): void {
    const tfButtons = [
      { id: 'btn-tf-1m', tf: 60 as const },
      { id: 'btn-tf-5m', tf: 300 as const },
      { id: 'btn-tf-10m', tf: 600 as const },
    ];
    const allBtns = tfButtons.map(b => document.getElementById(b.id)!);

    for (let i = 0; i < tfButtons.length; i++) {
      const { tf } = tfButtons[i]!;
      allBtns[i]!.addEventListener('click', () => {
        this.stockChart.setTimeframe(tf);
        allBtns.forEach(b => b.classList.remove('active'));
        allBtns[i]!.classList.add('active');
      });
    }

    // Chart type toggle
    const btnCandle = document.getElementById('btn-candle')!;
    const btnLine = document.getElementById('btn-line')!;
    btnCandle.addEventListener('click', () => {
      this.stockChart.setMode('candle');
      btnCandle.classList.add('active');
      btnLine.classList.remove('active');
    });
    btnLine.addEventListener('click', () => {
      this.stockChart.setMode('line');
      btnLine.classList.add('active');
      btnCandle.classList.remove('active');
    });

    const selectorEl = document.getElementById('stock-selector')!;
    const stocks = this.gameState.stockMarket.stocks;
    for (const s of stocks) {
      const btn = document.createElement('button');
      btn.textContent = s.symbol;
      btn.dataset['sym'] = s.symbol;
      const sectorLabel = s.sector.charAt(0).toUpperCase() + s.sector.slice(1);
      btn.title = `${s.name} — ${sectorLabel}\n${s.description}`;
      if (s.symbol === this.gameState.stockMarket.selectedSymbol) btn.classList.add('active');
      btn.addEventListener('click', () => {
        this.gameState.stockMarket.selectedSymbol = s.symbol;
        selectorEl.querySelectorAll('button').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
      });
      selectorEl.appendChild(btn);
    }
  }

  private setupTabs(): void {
    const tabBar = document.getElementById('tab-bar');
    if (!tabBar) return;

    const tabs = tabBar.querySelectorAll('button');
    const sidebar = document.getElementById('sidebar')!;
    const mainPanel = document.getElementById('main-panel')!;

    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const target = tab.dataset['tab'];
        if (!target) return;
        tabs.forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
        sidebar.classList.remove('active-tab');
        mainPanel.classList.remove('active-tab', 'active-tab-code', 'active-tab-output');
        if (target === 'sidebar') {
          sidebar.classList.add('active-tab');
        } else if (target === 'code') {
          mainPanel.classList.add('active-tab-code');
        } else if (target === 'output') {
          mainPanel.classList.add('active-tab-output');
        }
      });
    });
  }

  async init(): Promise<void> {
    try {
      await this.sandbox.init(this.gameState);
      this.consoleOutput.appendSystem('Sandbox ready. Write code and click RUN.');
    } catch (err) {
      this.consoleOutput.appendError(`Sandbox failed to load: ${err}`);
      this.consoleOutput.appendSystem('You can still use sidebar buttons.');
    }
  }

  private getStepDelay(): number {
    const hw = this.gameState.hardware;
    switch (hw.type) {
      case 'cpu': return Math.max(20, 150 - hw.clockSpeed * 5);
      case 'gpu': return Math.max(10, 60 - hw.cores * 0.1);
      case 'quantum': return 5;
      case 'subatomic': return 2;
    }
  }

  private stopExecution(): void {
    this.editor.abort();
    this.executing = false;
    this.runBtn.textContent = 'RUN';
    this.runBtn.style.opacity = '1';
    this.runBtn.style.background = '';
    this.consoleOutput.appendSystem('--- ABORTED ---');
  }

  private runCode(code: string): void {
    if (!this.sandbox.isReady()) {
      this.consoleOutput.appendError('Sandbox still loading...');
      return;
    }

    // Snapshot the lines at run time (editor can change freely after this)
    const snapshotLines = code.split('\n');

    // Snapshot stock prices at execution time for mission validation
    const priceSnapshot: Record<string, number> = {};
    for (const s of this.gameState.stockMarket.stocks) {
      priceSnapshot[s.symbol] = Math.round(s.price * 100) / 100;
    }

    const trace = this.sandbox.executeTraced(code, this.gameState);

    if (trace.steps.length === 0 && trace.error) {
      this.consoleOutput.appendError(trace.error.text);
      return;
    }

    this.executing = true;
    this.runBtn.textContent = 'STOP';
    this.runBtn.style.opacity = '1';
    this.runBtn.style.background = '#aa3333';
    this.consoleOutput.appendSystem('--- EXECUTING ---');

    const speed = this.getStepDelay();

    const outputsByStep = new Map<number, typeof trace.outputs>();
    for (const o of trace.outputs) {
      const arr = outputsByStep.get(o.stepIndex) ?? [];
      arr.push(o);
      outputsByStep.set(o.stepIndex, arr);
    }

    this.editor.replayTrace(trace.steps, speed, {
      onStep: (stepIndex, lineNumber) => {
        // Use snapshot lines for VFX detection (not live editor)
        const lineText = snapshotLines[lineNumber - 1] ?? '';
        const trimmed = lineText.trim();
        if (trimmed !== '') this.detectVFX(trimmed);

        const outputs = outputsByStep.get(stepIndex);
        if (outputs) {
          for (const o of outputs) {
            this.consoleOutput.append(o.entry);
          }
        }
      },

      onDone: () => {
        this.executing = false;

        if (trace.error) {
          this.consoleOutput.append(trace.error);
        }

        this.consoleOutput.appendSystem('--- DONE ---');

        const allOutputs = trace.outputs.map(o => o.entry.text);
        const readyMissions = this.gameState.checkMissions(allOutputs, priceSnapshot);
        for (const m of readyMissions) {
          if (!m.savedCode) {
            m.savedCode = code;
            this.gameState.save();
          }
        }
        if (readyMissions.length > 0) {
          this.showCollectToast(readyMissions);
        }

        this.runBtn.textContent = 'RUN';
        this.runBtn.style.opacity = '1';
        this.runBtn.style.background = '';
      },

      onAborted: () => {
        this.executing = false;
        this.runBtn.textContent = 'RUN';
        this.runBtn.style.opacity = '1';
        this.runBtn.style.background = '';
      },
    });
  }

  private detectVFX(_line: string): void {
    // VFX triggers reserved for future 3D effects
  }

  start(): void {
    let lastTime = performance.now();
    const animate = () => {
      requestAnimationFrame(animate);
      const now = performance.now();
      const delta = (now - lastTime) / 1000;
      lastTime = now;
      this.gameState.update(delta);
      this.sidebar.update();
      this.updateTopStatusBar();

      const stock = this.gameState.stockMarket.getSelectedStock();
      const emotion = this.gameState.stockMarket.getMarketEmotion();
      this.stockChart.render(stock, emotion);
    };
    animate();
  }

  private updateTopStatusBar(): void {
    const s = this.gameState;
    const m = document.getElementById('ts-money');
    const c = document.getElementById('ts-credits');
    const y = document.getElementById('ts-year');
    const e = document.getElementById('ts-era');
    if (m) m.textContent = '$' + s.money.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (c) c.textContent = s.researchCredits + ' credits';
    if (y) y.textContent = String(s.year);
    if (e) e.textContent = s.getEraName();
  }

  private updateFileLabel(): void {
    const label = document.getElementById('current-file-name');
    if (!label) return;
    const active = this.editor.getScripts().find(s => s.id === this.editor.getActiveScriptId());
    label.textContent = active?.name ?? 'untitled';
  }

  private setupFileManager(): void {
    document.getElementById('btn-file-manager')?.addEventListener('click', () => {
      this.showFileManagerModal();
    });
  }

  private showFileManagerModal(): void {
    document.getElementById('file-manager-modal')?.remove();

    const modal = document.createElement('div');
    modal.id = 'file-manager-modal';

    const renderFiles = () => {
      const scripts = this.editor.getScripts();
      const activeId = this.editor.getActiveScriptId();
      const body = modal.querySelector('.nm-body');
      if (!body) return;

      body.innerHTML = scripts.map(s => {
        const date = new Date(s.updatedAt);
        const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        return `<div class="fm-file${s.id === activeId ? ' active' : ''}" data-id="${s.id}">
          <div>
            <div class="fm-file-name">${s.name}</div>
            <div style="color:#334455;font-size:12px;">${s.code.split('\\n').length} lines \u00B7 ${time}</div>
          </div>
          <div class="fm-file-actions">
            <button class="fm-file-btn fm-rename" title="Rename">\u270F</button>
            <button class="fm-file-btn fm-delete delete" title="Delete">\u2715</button>
          </div>
        </div>`;
      }).join('') +
      `<button class="sidebar-btn" id="fm-new" style="margin:8px 12px;text-align:center;border-color:#336655;color:#00ff88;">+ New Script</button>`;

      // Wire up events
      body.querySelectorAll('.fm-file').forEach(el => {
        const id = (el as HTMLElement).dataset['id']!;
        el.addEventListener('click', (e) => {
          if ((e.target as HTMLElement).closest('.fm-file-btn')) return;
          if (this.executing) this.stopExecution();
          this.editor.switchTo(id);
          this.updateFileLabel();
          renderFiles();
        });
        el.querySelector('.fm-rename')?.addEventListener('click', () => {
          const script = this.editor.getScripts().find(s => s.id === id);
          if (!script) return;
          const name = prompt('Rename:', script.name);
          if (name && name !== script.name) {
            this.editor.renameScript(id, name);
            this.updateFileLabel();
            renderFiles();
          }
        });
        el.querySelector('.fm-delete')?.addEventListener('click', () => {
          if (this.editor.getScripts().length <= 1) { alert('Cannot delete the last script.'); return; }
          const script = this.editor.getScripts().find(s => s.id === id);
          if (script && confirm(`Delete "${script.name}"?`)) {
            if (this.executing) this.stopExecution();
            this.editor.deleteScript(id);
            this.updateFileLabel();
            renderFiles();
          }
        });
      });
      body.querySelector('#fm-new')?.addEventListener('click', () => {
        const name = prompt('Script name:', `script_${this.editor.getScripts().length + 1}.js`);
        if (name) {
          if (this.executing) this.stopExecution();
          this.editor.newScript(name);
          this.updateFileLabel();
          renderFiles();
        }
      });
    };

    modal.innerHTML = `
      <div class="nm-backdrop"></div>
      <div class="nm-content" style="max-width:450px;height:auto;max-height:80%;">
        <div class="nm-header"><span>\u{1F4C1} Scripts</span><button class="nm-close">&times;</button></div>
        <div class="nm-body" style="overflow-y:auto;"></div>
      </div>
    `;

    document.body.appendChild(modal);
    modal.querySelector('.nm-backdrop')!.addEventListener('click', () => modal.remove());
    modal.querySelector('.nm-close')!.addEventListener('click', () => modal.remove());
    renderFiles();
  }

  /** Show a "ready to collect" notification toast — handles single or multiple missions */
  private showCollectToast(missions: import('../game/missions/Missions').Mission[]): void {
    // Remove any existing collect toast
    document.querySelector('.mission-collect-toast')?.remove();

    // Inject animation keyframes if not already present
    if (!document.getElementById('mission-toast-style')) {
      const style = document.createElement('style');
      style.id = 'mission-toast-style';
      style.textContent = `
        @keyframes toast-in { from { opacity:0; transform:translateX(-50%) translateY(-20px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }
        @keyframes toast-out { from { opacity:1; } to { opacity:0; transform:translateX(-50%) translateY(-20px); } }
        @keyframes confetti-fall { 0% { transform:translateY(0) rotate(0deg); opacity:1; } 100% { transform:translateY(100vh) rotate(720deg); opacity:0; } }
      `;
      document.head.appendChild(style);
    }

    const toast = document.createElement('div');
    toast.className = 'mission-collect-toast';

    const count = missions.length;
    const title = count === 1 ? 'MISSION READY' : `${count} MISSIONS READY`;
    const listHtml = missions.map(m => {
      const reward = `+${m.researchCredits} cr` + (m.moneyReward > 0 ? ` +$${m.moneyReward.toLocaleString()}` : '');
      const cost = m.collectCost ? ` <span style="color:var(--skin-energy);">(costs $${m.collectCost.toLocaleString()})</span>` : '';
      return `<div style="margin:4px 0;padding:4px 0;${count > 1 ? 'border-bottom:1px solid var(--skin-border);' : ''}">
        <div style="color:var(--skin-text-bright);font-size:12px;">${m.name}${cost}</div>
        <div style="color:var(--skin-credits);font-size:10px;">${reward}</div>
      </div>`;
    }).join('');

    toast.innerHTML = `
      <div style="font-size:24px;margin-bottom:6px;">&#11088;</div>
      <div style="color:var(--skin-energy);font-weight:bold;font-size:14px;">${title}</div>
      ${listHtml}
      <div style="color:var(--skin-text-mid);font-size:10px;margin-top:8px;">Collect from the Missions panel</div>
      <button class="toast-dismiss-btn" style="
        margin-top:8px; padding:4px 16px;
        background: none; border: 1px solid var(--skin-border);
        border-radius: var(--skin-radius); color: var(--skin-text-dim); font-size: 11px;
        cursor: pointer;
      ">&times; Dismiss</button>
    `;
    toast.style.cssText = `
      position: fixed; top: 18%; left: 50%; transform: translateX(-50%);
      background: var(--skin-bg-modal);
      border: 1px solid var(--skin-border); border-radius: var(--skin-radius);
      padding: 20px 36px; text-align: center; z-index: 10000;
      max-width: 400px; width: 90%;
      box-shadow: 0 8px 32px rgba(0,0,0,0.5);
      animation: toast-in 0.4s ease-out;
      pointer-events: auto;
    `;

    document.body.appendChild(toast);

    toast.querySelector('.toast-dismiss-btn')?.addEventListener('click', () => toast.remove());
    toast.addEventListener('click', (e) => {
      if (e.target === toast) toast.remove();
    });

    for (const m of missions) {
      this.consoleOutput.appendSystem(`MISSION READY: ${m.name} — Collect from the Missions panel!`);
    }
  }

  /** Attempt to collect a mission, show confetti on success or error on fail */
  doCollectMission(missionId: string, toastEl?: HTMLElement): void {
    const result = this.gameState.collectMission(missionId);
    if (result.success) {
      const mission = this.gameState.missions.find(m => m.id === missionId)!;
      if (toastEl) toastEl.remove();

      // Confetti + completion toast
      this.spawnConfetti();
      const completionToast = document.createElement('div');
      const reward = `+${mission.researchCredits} cr` + (mission.moneyReward > 0 ? ` +$${mission.moneyReward.toLocaleString()}` : '');
      completionToast.innerHTML = `
        <div style="font-size:24px;margin-bottom:6px;">&#127881;</div>
        <div style="color:var(--skin-money);font-weight:bold;font-size:14px;">MISSION COMPLETE!</div>
        <div style="color:var(--skin-text-bright);font-size:12px;margin:4px 0;">${mission.name}</div>
        <div style="color:var(--skin-credits);font-size:11px;">${reward}</div>
      `;
      completionToast.style.cssText = `
        position: fixed; top: 18%; left: 50%; transform: translateX(-50%);
        background: var(--skin-bg-modal);
        border: 1px solid var(--skin-accent); border-radius: var(--skin-radius);
        padding: 20px 36px; text-align: center; z-index: 10000;
        box-shadow: 0 8px 32px rgba(0,0,0,0.5);
        animation: toast-in 0.4s ease-out, toast-out 0.5s ease-in 2.5s forwards;
        pointer-events: none;
      `;
      document.body.appendChild(completionToast);
      setTimeout(() => completionToast.remove(), 3200);

      this.consoleOutput.appendSystem(`MISSION COMPLETE: ${mission.name}!`);
      this.consoleOutput.appendLog(`+${mission.researchCredits} research credits` + (mission.moneyReward > 0 ? ` +$${mission.moneyReward.toLocaleString()}` : ''));
    } else {
      this.consoleOutput.appendError(result.message);
      // Shake the collect button if toast exists
      if (toastEl) {
        const btn = toastEl.querySelector('.collect-btn') as HTMLElement;
        if (btn) {
          btn.style.borderColor = '#ff4444';
          btn.style.color = '#ff4444';
          setTimeout(() => { btn.style.borderColor = '#44dd88'; btn.style.color = '#44dd88'; }, 600);
        }
      }
    }
  }

  private spawnConfetti(): void {
    const colors = ['#44dd88', '#aa88ff', '#ffcc44', '#ff6688', '#44bbff', '#ff8844'];
    const container = document.createElement('div');
    container.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:9999;overflow:hidden;';

    for (let i = 0; i < 60; i++) {
      const piece = document.createElement('div');
      const color = colors[Math.floor(Math.random() * colors.length)]!;
      const left = Math.random() * 100;
      const delay = Math.random() * 0.8;
      const duration = 1.5 + Math.random() * 1.5;
      const size = 4 + Math.random() * 6;
      const shape = Math.random() > 0.5 ? '50%' : '0';
      piece.style.cssText = `
        position:absolute; top:-10px; left:${left}%;
        width:${size}px; height:${size}px;
        background:${color}; border-radius:${shape};
        animation: confetti-fall ${duration}s ease-in ${delay}s forwards;
        opacity:0;
      `;
      // Make it visible after delay
      piece.style.animationFillMode = 'forwards';
      piece.style.opacity = '0';
      piece.style.animation = `confetti-fall ${duration}s ease-in ${delay}s`;
      container.appendChild(piece);
    }

    document.body.appendChild(container);
    setTimeout(() => container.remove(), 4000);
  }
}
